import { useState } from 'react'
import { EmailReadiness, EmailReadinessCheck, EmailReadinessSection } from '../../types'

const TONE: Record<string, { dot: string; label: string; text: string }> = {
  off: { dot: 'bg-stone', label: 'Off', text: 'text-text-primary' },
  test_only: { dot: 'bg-stone', label: 'Test only', text: 'text-text-primary' },
  ok: { dot: 'bg-[#15532E]', label: 'Ready', text: 'text-[#15532E]' },
  warn: { dot: 'bg-[#B8860B]', label: 'Optional', text: 'text-[#8A6A12]' },
  blocked: { dot: 'bg-[#ED1C24]', label: 'Blocker', text: 'text-[#ED1C24]' },
  unverified: { dot: 'bg-[#8A8480]', label: 'Unverified', text: 'text-text-muted' },
}

function tone(status: string) {
  return TONE[status] ?? TONE.unverified
}

function StatusPill({ status }: { status: string }) {
  const t = tone(status)
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap">
      <span className={`w-2 h-2 rounded-full ${t.dot}`} aria-hidden="true" />
      <span className={t.text}>{t.label}</span>
    </span>
  )
}

/**
 * Keeps four kinds of evidence visibly apart. Saved settings and local
 * cryptography checks are things Pinnacle knows on its own; provider
 * acceptance and inbound signed traffic are the only live proof that the
 * integration works. Anything Pinnacle cannot observe stays "Unverified"
 * with the operator instructions needed to resolve it externally.
 */
export default function EmailReadinessPanel({
  readiness,
  webhookUrl,
}: {
  readiness: EmailReadiness
  webhookUrl: string
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="bg-white border border-stone/20 rounded-lg p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-base font-semibold text-text-primary">Readiness</h2>
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="text-xs font-semibold text-text-primary underline whitespace-nowrap"
        >
          {expanded ? 'Hide details' : 'Show details'}
        </button>
      </div>
      <p className="text-xs text-text-muted mb-4">
        Saved mode: {{ off: 'Off', test_only: 'Test only', live: 'Live' }[readiness.delivery_mode]}.{' '}
        Saved configuration and local checks are separated from live evidence on purpose. A stored API key or
        a passing signature self-test proves nothing about whether SendGrid will actually deliver mail.
        Test results do not enable or establish readiness for Live customer delivery.
      </p>

      <div className="space-y-3">
        <Section
          title="1 · Saved configuration"
          subtitle="Local fact — what is stored in Pinnacle."
          section={readiness.saved_configuration}
          expanded={expanded}
        />
        <Section
          title="Guide sender configuration"
          subtitle="Local fact — Guide invitations, account messages and Guide billing use this separate sender."
          section={readiness.guide_sender}
          expanded={expanded}
        />
        <Section
          title="2 · Sample provider acceptance"
          subtitle="Live evidence — SendGrid's Mail Send API accepted a real test message."
          section={readiness.provider_acceptance}
          expanded={expanded}
        />
        <Section
          title="3 · Local webhook checks"
          subtitle="Local fact — the signing key parses and this server can verify ECDSA. SendGrid is not contacted."
          section={readiness.local_webhook}
          expanded={expanded}
        />
        <Section
          title="4 · Inbound signed traffic"
          subtitle="Live evidence — a signature-verified event webhook request actually arrived."
          section={readiness.inbound_signed_traffic}
          expanded={expanded}
        />
        <Section
          title="Background worker"
          subtitle="Test emails and recaps are queued, never sent on the request path."
          section={readiness.worker_processing}
          expanded={expanded}
        />
        <Section
          title="Public URL for links"
          subtitle="Used for every ordinary navigation link inside outbound email."
          section={readiness.public_host}
          expanded={expanded}
        />
      </div>

      <div className="mt-5 pt-4 border-t border-stone/10">
        <h3 className="text-sm font-semibold text-text-primary mb-1">External prerequisites</h3>
        <p className="text-xs text-text-muted mb-3">
          Pinnacle deliberately does not change your SendGrid account. These steps stay explicitly unverified
          until you complete them there. Webhook URL: <code className="font-mono break-all">{webhookUrl}</code>
        </p>
        <ul className="space-y-3">
          {readiness.external_prerequisites.map(p => (
            <li key={p.name} className="border border-stone/15 rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-semibold text-text-primary">{p.name}</span>
                <StatusPill status={p.status} />
              </div>
              <p className="text-xs text-text-muted mt-1">{p.detail}</p>
              <p className="text-xs text-text-primary mt-1.5">{p.instructions}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function Section({
  title,
  subtitle,
  section,
  expanded,
}: {
  title: string
  subtitle: string
  section: EmailReadinessSection
  expanded: boolean
}) {
  return (
    <div className="border border-stone/15 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-text-primary">{title}</div>
          <div className="text-xs text-text-muted">{subtitle}</div>
        </div>
        <div className="text-right">
          <StatusPill status={section.status} />
          {section.at_display && <div className="text-xs text-text-muted mt-0.5">{section.at_display}</div>}
        </div>
      </div>
      <p className="text-xs text-text-muted mt-2">{section.detail}</p>
      {expanded && section.checks && section.checks.length > 0 && (
        <ul className="mt-2 space-y-1.5 border-t border-stone/10 pt-2">
          {section.checks.map((c: EmailReadinessCheck) => (
            <li key={c.name} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-text-primary">{c.name}</div>
                <div className="text-xs text-text-muted">{c.detail}</div>
              </div>
              <StatusPill status={c.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
